/**
 * Delete (or detach) something on X behind a confirm dialog. Deleting on X is permanent.
 * Clicks never reach the row underneath (rows expand on click).
 */
import { useState } from 'react';
import { Loader2, Trash2, Unlink } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';
import { alreadyToasted, errMsg } from '@/services/x-ads.service';

export function ConfirmDialog({ open, title, message, danger = false, confirmLabel, onCancel, onConfirm, busy = false }: {
  open: boolean; title: string; message: string; danger?: boolean; confirmLabel?: string; onCancel: () => void; onConfirm: () => void; busy?: boolean;
}) {
  return <Modal open={open} onClose={onCancel} title={title} widthClass="max-w-md">
    <p className="text-sm leading-relaxed text-text-secondary">{message}</p>
    <div className="mt-5 flex justify-end gap-2">
      <Button variant="secondary" onClick={onCancel} disabled={busy}>Cancel</Button>
      <Button variant={danger ? 'danger' : 'primary'} onClick={onConfirm} loading={busy}>{confirmLabel ?? (danger ? 'Delete' : 'Confirm')}</Button>
    </div>
  </Modal>;
}

export function DeleteButton({ what, name, message, detach = false, onDelete, onDone, disabled }: {
  what: string;
  name?: string;
  /** Overrides the default "permanent on X" message. */
  message?: string;
  /** Detach (remove from a line item) rather than delete. */
  detach?: boolean;
  onDelete: () => Promise<unknown>;
  onDone?: () => unknown;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const verb = detach ? 'Remove' : 'Delete';

  const confirm = async () => {
    setOpen(false);
    setBusy(true);
    try {
      await onDelete();
      toast.success(`${what.charAt(0).toUpperCase()}${what.slice(1)} ${detach ? 'removed' : 'deleted'}`);
      await onDone?.();
    } catch (e) {
      if (!alreadyToasted(e)) toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return <span className="inline-flex" onClick={(e) => e.stopPropagation()}>
    <button type="button" title={`${verb} ${what}`} aria-label={`${verb} ${what}${name ? ` ${name}` : ''}`} disabled={disabled || busy} onClick={() => setOpen(true)}
      className="flex h-8 w-8 items-center justify-center rounded-lg text-text-muted transition-colors hover:bg-danger-soft hover:text-danger disabled:cursor-not-allowed disabled:opacity-40">
      {busy ? <Loader2 size={14} className="animate-spin" /> : detach ? <Unlink size={14} /> : <Trash2 size={14} />}
    </button>
    <ConfirmDialog open={open} danger title={`${verb} ${what}?`} confirmLabel={verb}
      message={message ?? `${name ? `"${name}"` : `This ${what}`} will be deleted on X. This is permanent and cannot be undone.`}
      onCancel={() => setOpen(false)} onConfirm={confirm} />
  </span>;
}
